import React, { useState, useRef, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  EditButton,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  ReferenceInput,
  ImageField,
  ImageInput,
  ArrayInput,
  SimpleFormIterator,
  BooleanInput,
  Filter,
  TopToolbar,
  CreateButton,
  required,
  minValue,
  number,
  Show,
  ReferenceField,
  ChipField,
  FunctionField,
  useRecordContext,
  BulkDeleteButton,
  useNotify,
  useRefresh,
  useListContext,
  SimpleList,
  SearchInput,
  FormDataConsumer,
  useGetList,
  useInput,
} from 'react-admin';
import {
  Box,
  Chip,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  useMediaQuery,
  Theme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import ImageIcon from '@mui/icons-material/Image';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// 상품 필터 - 통합 검색 기능
const ProductFilter = [
  <SearchInput
    key="q"
    source="q"
    alwaysOn
    placeholder="상품명, 브랜드, ID, 설명으로 통합 검색..."
    sx={{
      minWidth: 350,
      '& .MuiInputBase-root': {
        backgroundColor: '#f8f9fa',
        borderRadius: 1,
      },
    }}
  />,
  <TextInput key="brand" source="brand" label="브랜드" />,
  <NumberInput key="minPrice" source="minPrice" label="최소 가격" />,
  <NumberInput key="maxPrice" source="maxPrice" label="최대 가격" />,
  <BooleanInput key="isActive" source="isActive" label="판매중만" />,
  <BooleanInput key="isRecommended" source="isRecommended" label="추천상품" />,
  <BooleanInput key="isBest" source="isBest" label="베스트상품" />,
  <BooleanInput key="isNew" source="isNew" label="신상품" />,
  <BooleanInput key="naverShoppingExposed" source="naverShoppingExposed" label="네이버쇼핑 노출" />,
];

// 엑셀 임포트 버튼
const ImportButton = () => {
  const [uploading, setUploading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Next.js API 호출
      // 로컬: Vite proxy 사용 (VITE_API_URL이 없으면 빈 문자열)
      // 프로덕션: VITE_API_URL 환경 변수 사용 (https://shop.catchu.kr)
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/v1/admin/products/import-excel`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        notify(result.message, { type: 'success' });
        refresh();
      } else {
        notify(result.message || '업로드 실패', { type: 'error' });
      }
    } catch (error: any) {
      notify(`업로드 오류: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
      // 파일 input 초기화
      event.target.value = '';
    }
  };

  return (
    <>
      <input
        type="file"
        id="excel-upload"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <label htmlFor="excel-upload">
        <Button
          component="span"
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadFileIcon />}
          variant="outlined"
          sx={{ ml: 1 }}
        >
          {uploading ? '업로드 중...' : '엑셀 업로드'}
        </Button>
      </label>
    </>
  );
};

// 리스트 액션 툴바
const ListActions = () => (
  <TopToolbar>
    <CreateButton label="생성" />
    <ImportButton />
  </TopToolbar>
);

// 커스텀 판매 시작 버튼
const BulkActivateButton = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const { selectedIds } = useListContext();

  const handleClick = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify('상품을 선택해주세요.', { type: 'warning' });
      return;
    }

    setLoading(true);
    console.log('[BulkActivate] Starting update:', { ids: selectedIds, count: selectedIds.length });

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/products/bulk-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: selectedIds,
          isActive: true,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || '상품 상태 업데이트에 실패했습니다.');
      }

      const updatedCount = result?.data?.updatedCount ?? selectedIds.length;

      console.log('[BulkActivate] Update success:', result);

      notify(`${updatedCount}개 상품이 판매 시작되었습니다.`, { type: 'success' });

      // 페이지 새로고침으로 확실하게 갱신
      setTimeout(() => {
        console.log('[BulkActivate] Reloading page...');
        window.location.reload();
      }, 800);

    } catch (error: any) {
      console.error('[BulkActivate] Error:', error);
      notify(`오류 발생: ${error.message}`, { type: 'error' });
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={loading || !selectedIds || selectedIds.length === 0}
      sx={{
        backgroundColor: '#4CAF50',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#45A049',
        },
        '&:disabled': {
          backgroundColor: '#A5D6A7',
          color: '#FFFFFF',
        }
      }}
    >
      {loading ? '처리 중...' : '판매 시작'}
    </Button>
  );
};

// 커스텀 판매 중지 버튼
const BulkDeactivateButton = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const { selectedIds } = useListContext();

  const handleClick = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify('상품을 선택해주세요.', { type: 'warning' });
      return;
    }

    setLoading(true);
    console.log('[BulkDeactivate] Starting update:', { ids: selectedIds, count: selectedIds.length });

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/products/bulk-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: selectedIds,
          isActive: false,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || '상품 상태 업데이트에 실패했습니다.');
      }

      const updatedCount = result?.data?.updatedCount ?? selectedIds.length;

      console.log('[BulkDeactivate] Update success:', result);

      notify(`${updatedCount}개 상품이 판매 중지되었습니다.`, { type: 'success' });

      // 페이지 새로고침으로 확실하게 갱신
      setTimeout(() => {
        console.log('[BulkDeactivate] Reloading page...');
        window.location.reload();
      }, 800);

    } catch (error: any) {
      console.error('[BulkDeactivate] Error:', error);
      notify(`오류 발생: ${error.message}`, { type: 'error' });
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={loading || !selectedIds || selectedIds.length === 0}
      sx={{
        backgroundColor: '#F97316',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#EA580C',
        },
        '&:disabled': {
          backgroundColor: '#FFAB91',
          color: '#FFFFFF',
        }
      }}
    >
      {loading ? '처리 중...' : '판매 중지'}
    </Button>
  );
};

// 커스텀 네이버 쇼핑 노출 활성화 버튼
const BulkNaverExposeButton = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const { selectedIds } = useListContext();

  const handleClick = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify('상품을 선택해주세요.', { type: 'warning' });
      return;
    }

    setLoading(true);
    console.log('[BulkNaverExpose] Starting update:', { ids: selectedIds, count: selectedIds.length });

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/products/bulk-naver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: selectedIds,
          naverShoppingExposed: true,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || '네이버 쇼핑 노출 설정에 실패했습니다.');
      }

      const updatedCount = result?.data?.updatedCount ?? selectedIds.length;

      console.log('[BulkNaverExpose] Update success:', result);

      notify(`${updatedCount}개 상품이 네이버 쇼핑에 노출되었습니다.`, { type: 'success' });

      setTimeout(() => {
        console.log('[BulkNaverExpose] Reloading page...');
        window.location.reload();
      }, 800);

    } catch (error: any) {
      console.error('[BulkNaverExpose] Error:', error);
      notify(`오류 발생: ${error.message}`, { type: 'error' });
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={loading || !selectedIds || selectedIds.length === 0}
      sx={{
        backgroundColor: '#10B981',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#059669',
        },
        '&:disabled': {
          backgroundColor: '#A7F3D0',
          color: '#FFFFFF',
        }
      }}
    >
      {loading ? '처리 중...' : '네이버 허용'}
    </Button>
  );
};

// 커스텀 네이버 쇼핑 노출 비활성화 버튼
const BulkNaverHideButton = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const { selectedIds } = useListContext();

  const handleClick = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify('상품을 선택해주세요.', { type: 'warning' });
      return;
    }

    setLoading(true);
    console.log('[BulkNaverHide] Starting update:', { ids: selectedIds, count: selectedIds.length });

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/products/bulk-naver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: selectedIds,
          naverShoppingExposed: false,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || '네이버 쇼핑 노출 해제에 실패했습니다.');
      }

      const updatedCount = result?.data?.updatedCount ?? selectedIds.length;

      console.log('[BulkNaverHide] Update success:', result);

      notify(`${updatedCount}개 상품이 네이버 쇼핑에서 해제되었습니다.`, { type: 'success' });

      setTimeout(() => {
        console.log('[BulkNaverHide] Reloading page...');
        window.location.reload();
      }, 800);

    } catch (error: any) {
      console.error('[BulkNaverHide] Error:', error);
      notify(`오류 발생: ${error.message}`, { type: 'error' });
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={loading || !selectedIds || selectedIds.length === 0}
      sx={{
        backgroundColor: '#F59E0B',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#D97706',
        },
        '&:disabled': {
          backgroundColor: '#FDE68A',
          color: '#FFFFFF',
        }
      }}
    >
      {loading ? '처리 중...' : '네이버 해제'}
    </Button>
  );
};

// 벌크 액션 버튼
const BulkActionButtons = () => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
    <BulkActivateButton />
    <BulkDeactivateButton />
    <BulkNaverExposeButton />
    <BulkNaverHideButton />
    <BulkDeleteButton label="삭제" mutationMode="pessimistic" />
  </Box>
);

// 페이지/필터 변경 시 선택 초기화하는 Datagrid wrapper
const ProductDatagridWithReset = () => {
  const { page, filterValues, onUnselectItems } = useListContext();
  const prevPage = useRef(page);
  const prevFilterValues = useRef(filterValues);
  const isMounted = useRef(false);

  // 컴포넌트 마운트 시 선택 초기화 (메뉴 이동 후 돌아왔을 때)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      onUnselectItems();
    }
  }, [onUnselectItems]);

  // 페이지/필터 변경 시 선택 초기화
  useEffect(() => {
    const filterChanged = JSON.stringify(prevFilterValues.current) !== JSON.stringify(filterValues);
    const pageChanged = prevPage.current !== page;

    if (filterChanged || pageChanged) {
      onUnselectItems();
    }

    prevPage.current = page;
    prevFilterValues.current = filterValues;
  }, [page, filterValues, onUnselectItems]);

  return (
    <Datagrid rowClick="show" bulkActionButtons={<BulkActionButtons />}>
      <TextField source="name" label="상품명" />
      <ReferenceField source="categoryId" reference="categories" label="카테고리1" link={false} sx={{ whiteSpace: 'nowrap' }}>
        <FunctionField render={(record: any) => {
          if (!record?.description) return '-';
          const parts = record.description.split(' > ');
          return parts[0] || '-';
        }} />
      </ReferenceField>
      <ReferenceField source="categoryId" reference="categories" label="카테고리2" link={false}>
        <FunctionField render={(record: any) => {
          if (!record?.description) return '-';
          const parts = record.description.split(' > ');
          return parts[1] ? <Chip label={parts[1]} size="small" variant="outlined" /> : '-';
        }} />
      </ReferenceField>
      <TextField source="brand" label="Brand" sx={{ textAlign: 'center' }} />
      <NumberField
        source="price"
        label="가격"
        options={{
          style: 'currency',
          currency: 'KRW',
        }}
        sx={{ textAlign: 'right' }}
      />
      <NumberField
        source="discountPrice"
        label="판매가"
        options={{
          style: 'currency',
          currency: 'KRW',
        }}
        sx={{ textAlign: 'right' }}
      />
      <NumberField source="stock" label="재고" sx={{ textAlign: 'right' }} />
      <FunctionField
        label="상태"
        sx={{ textAlign: 'center' }}
        render={(record: any) => {
          let label = '판매중';
          let color: 'success' | 'default' | 'error' = 'success';

          if (!record.isActive) {
            label = '판매중지';
            color = 'default';
          } else if (record.stock === 0) {
            label = '품절';
            color = 'error';
          }

          return (
            <Chip
              label={label}
              color={color}
              size="small"
            />
          );
        }}
      />
      <FunctionField
        label="네이버"
        sx={{ textAlign: 'center', minWidth: 80, whiteSpace: 'nowrap' }}
        render={(record: any) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {record.naverShoppingExposed ? (
              <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
            ) : (
              <CloseIcon sx={{ color: 'error.main', fontSize: 20 }} />
            )}
          </Box>
        )}
      />
      <FunctionField
        source="createdAt"
        label="등록일"
        sx={{ textAlign: 'center' }}
        render={(record: any) => {
          if (!record.createdAt) return '-';
          const date = new Date(record.createdAt);
          return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).replace(/\. /g, '.').replace(/\.$/, '');
        }}
      />
    </Datagrid>
  );
};

// 모바일용 SimpleList with reset
const ProductSimpleListWithReset = () => {
  const { page, filterValues, onUnselectItems } = useListContext();
  const prevPage = useRef(page);
  const prevFilterValues = useRef(filterValues);
  const isMounted = useRef(false);

  // 컴포넌트 마운트 시 선택 초기화 (메뉴 이동 후 돌아왔을 때)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      onUnselectItems();
    }
  }, [onUnselectItems]);

  // 페이지/필터 변경 시 선택 초기화
  useEffect(() => {
    const filterChanged = JSON.stringify(prevFilterValues.current) !== JSON.stringify(filterValues);
    const pageChanged = prevPage.current !== page;

    if (filterChanged || pageChanged) {
      onUnselectItems();
    }

    prevPage.current = page;
    prevFilterValues.current = filterValues;
  }, [page, filterValues, onUnselectItems]);

  return (
    <SimpleList
      primaryText={(record) => record.name}
      secondaryText={(record) => {
        const price = record.discountPrice || record.price;
        return `${new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(price)} / 재고: ${record.stock}`;
      }}
      tertiaryText={(record) => {
        if (!record.isActive) return '판매중지';
        if (record.stock === 0) return '품절';
        return '판매중';
      }}
      linkType="show"
    />
  );
};

// 상품 목록
export const ProductList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E' }}>
          상품 목록
        </Typography>
      </Box>
      <List
        title="상품 목록"
        filters={ProductFilter}
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
        empty={false}
        disableSyncWithStore
        storeKey={false}
      >
        {isSmall ? <ProductSimpleListWithReset /> : <ProductDatagridWithReset />}
      </List>
    </>
  );
};

// 카테고리 선택 컴포넌트 (대분류 → 중분류)
const CategorySelectInputs = () => {
  const [parentCategoryId, setParentCategoryId] = useState<string | null>(null);

  // 모든 카테고리 가져오기
  const { data: allCategories, isLoading } = useGetList('categories', {
    sort: { field: 'displayOrder', order: 'ASC' },
    pagination: { page: 1, perPage: 500 },
  });

  // 클라이언트 사이드에서 필터링 (displayOrder 순서 유지)
  const parentChoices = allCategories
    ?.filter(cat => cat.level === 0)
    .map(cat => ({ id: cat.id, name: cat.name })) || [];

  const childChoices = allCategories
    ?.filter(cat => cat.parentId === parentCategoryId)
    .map(cat => ({ id: cat.id, name: cat.name })) || [];

  return (
    <Box sx={{
      display: 'flex',
      gap: 2,
      flexDirection: { xs: 'column', sm: 'row' },
      width: '100%',
      '& .MuiFormControl-root': { m: 0 }
    }}>
      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <SelectInput
          source="_parentCategory"
          label="카테고리1"
          choices={parentChoices}
          onChange={(e) => setParentCategoryId(e.target.value)}
          validate={required()}
          fullWidth
          isLoading={isLoading}
        />
      </Box>
      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <SelectInput
          source="categoryId"
          label="카테고리2"
          choices={childChoices}
          validate={required()}
          fullWidth
          disabled={!parentCategoryId}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

// 카테고리 선택 컴포넌트 (수정용 - 기존 카테고리 값 유지)
const CategorySelectInputsEdit = () => {
  const record = useRecordContext();
  const [parentCategoryId, setParentCategoryId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // 모든 카테고리 가져오기
  const { data: allCategories, isLoading } = useGetList('categories', {
    sort: { field: 'displayOrder', order: 'ASC' },
    pagination: { page: 1, perPage: 500 },
  });

  // 기존 categoryId로 대분류 초기화
  React.useEffect(() => {
    if (!initialized && record?.categoryId && allCategories) {
      const currentCategory = allCategories.find(cat => cat.id === record.categoryId);
      if (currentCategory?.parentId) {
        setParentCategoryId(currentCategory.parentId);
        setInitialized(true);
      }
    }
  }, [record?.categoryId, allCategories, initialized]);

  // 클라이언트 사이드에서 필터링
  const parentChoices = allCategories
    ?.filter(cat => cat.level === 0)
    .map(cat => ({ id: cat.id, name: cat.name })) || [];

  const childChoices = allCategories
    ?.filter(cat => cat.parentId === parentCategoryId)
    .map(cat => ({ id: cat.id, name: cat.name })) || [];

  return (
    <Box sx={{
      display: 'flex',
      gap: 2,
      flexDirection: { xs: 'column', sm: 'row' },
      width: '100%',
      '& .MuiFormControl-root': { m: 0 }
    }}>
      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <SelectInput
          source="_parentCategory"
          label="카테고리1"
          choices={parentChoices}
          defaultValue={parentCategoryId}
          onChange={(e) => setParentCategoryId(e.target.value)}
          validate={required()}
          fullWidth
          isLoading={isLoading}
        />
      </Box>
      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <SelectInput
          source="categoryId"
          label="카테고리2"
          choices={childChoices}
          validate={required()}
          fullWidth
          disabled={!parentCategoryId}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

// 폼 전용 필드 제거 (DB에 없는 필드들)
const transformProductData = (data: any) => {
  const { _parentCategory, shippingType, shippingFee, ...rest } = data;
  return rest;
};

// 상품 생성
export const ProductCreate = () => (
  <Create transform={transformProductData}>
    <SimpleForm>
      <Typography variant="h6" sx={{ mb: 2 }}>
        기본 정보
      </Typography>

      <TextInput source="name" label="상품명" validate={required()} fullWidth />

      <CategorySelectInputs />
      <TextInput source="brand" label="브랜드" fullWidth />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        가격 정보
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <NumberInput
            source="cost"
            label="공급가"
            validate={[number(), minValue(0)]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <NumberInput
            source="price"
            label="정가"
            validate={[required(), number(), minValue(0)]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <NumberInput
            source="discountPrice"
            label="판매가"
            validate={[number(), minValue(0)]}
            fullWidth
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        재고 관리
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <NumberInput
            source="stock"
            label="재고수량"
            validate={[required(), number(), minValue(0)]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <NumberInput
            source="maxPurchaseQuantity"
            label="1회 최대 구매수량"
            validate={[number(), minValue(1)]}
            defaultValue={99}
            helperText="고객이 한 번에 구매할 수 있는 최대 수량 (기본: 99개)"
            fullWidth
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        이미지
      </Typography>

      <TextInput source="imageUrl" label="대표 이미지 URL" fullWidth />
      <TextInput source="detailImageUrl" label="상세 이미지 URL" fullWidth />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        배송 정보
      </Typography>

      <FormDataConsumer>
        {({ formData }) => (
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            '& .MuiFormControl-root': { m: 0 }
          }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <SelectInput
                source="shippingType"
                label="배송타입"
                choices={[
                  { id: 'FREE', name: '무료배송' },
                  { id: 'PAID', name: '유료배송' },
                ]}
                defaultValue="FREE"
                validate={required()}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <NumberInput
                source="shippingFee"
                label="배송비"
                disabled={formData.shippingType === 'FREE'}
                fullWidth
              />
            </Box>
          </Box>
        )}
      </FormDataConsumer>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        판매 설정
      </Typography>

      <BooleanInput
        source="isActive"
        label="판매 활성화"
        helperText="비활성화하면 상품이 판매중지 상태가 됩니다"
        defaultValue={true}
      />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        네이버 쇼핑 설정
      </Typography>

      <BooleanInput
        source="naverShoppingExposed"
        label="네이버 쇼핑 노출"
        helperText="네이버 쇼핑에 상품을 노출합니다"
        defaultValue={false}
      />

      <FormDataConsumer>
        {({ formData }) => formData.naverShoppingExposed && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              네이버 쇼핑 상세 설정
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput
                  source="naverShoppingCategory"
                  label="네이버 카테고리 ID"
                  helperText="네이버 쇼핑 카테고리 코드"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  source="naverShoppingBrand"
                  label="네이버 브랜드명"
                  helperText="네이버에 표시될 브랜드명"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  source="naverShoppingModelNo"
                  label="모델번호"
                  helperText="상품 모델명/번호"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberInput
                  source="naverShipping"
                  label="배송비"
                  defaultValue={3000}
                  helperText="네이버 쇼핑 배송비 (기본 3,000원)"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SelectInput
                  source="naverCondition"
                  label="상품 상태"
                  choices={[
                    { id: '신상품', name: '신상품' },
                    { id: '중고', name: '중고' },
                    { id: '리퍼', name: '리퍼' },
                  ]}
                  defaultValue="신상품"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SelectInput
                  source="naverImportFlag"
                  label="수입 여부"
                  choices={[
                    { id: 'N', name: '국내산' },
                    { id: 'Y', name: '수입산' },
                  ]}
                  defaultValue="N"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  source="naverManufacturer"
                  label="제조사"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  source="naverOrigin"
                  label="원산지"
                  helperText="예: 대한민국, 중국 등"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SelectInput
                  source="naverAdult"
                  label="성인 상품"
                  choices={[
                    { id: 'N', name: '일반상품' },
                    { id: 'Y', name: '성인상품' },
                  ]}
                  defaultValue="N"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </FormDataConsumer>
    </SimpleForm>
  </Create>
);

// 상품 수정
export const ProductEdit = () => {
  const ProductTitle = () => {
    const record = useRecordContext();
    return <span>상품 수정: {record?.name}</span>;
  };

  return (
    <Edit title={<ProductTitle />} transform={transformProductData}>
      <SimpleForm>
        <Typography variant="h6" sx={{ mb: 2 }}>
          기본 정보
        </Typography>

        <TextInput source="id" label="ID" disabled />
        <TextInput source="name" label="상품명" validate={required()} fullWidth />

        <CategorySelectInputsEdit />
        <TextInput source="brand" label="브랜드" fullWidth />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          가격 정보
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <NumberInput
              source="cost"
              label="공급가"
              validate={[number(), minValue(0)]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <NumberInput
              source="price"
              label="정가"
              validate={[required(), number(), minValue(0)]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <NumberInput
              source="discountPrice"
              label="판매가"
              validate={[number(), minValue(0)]}
              fullWidth
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          재고 관리
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <NumberInput
              source="stock"
              label="재고수량"
              validate={[required(), number(), minValue(0)]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <NumberInput
              source="maxPurchaseQuantity"
              label="1회 최대 구매수량"
              validate={[number(), minValue(1)]}
              defaultValue={99}
              helperText="고객이 한 번에 구매할 수 있는 최대 수량 (기본: 99개)"
              fullWidth
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          이미지
        </Typography>

        <TextInput source="imageUrl" label="대표 이미지 URL" fullWidth />
        <TextInput source="detailImageUrl" label="상세 이미지 URL" fullWidth />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          배송 정보
        </Typography>

        <FormDataConsumer>
          {({ formData }) => (
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              width: '100%',
              '& .MuiFormControl-root': { m: 0 }
            }}>
              <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <SelectInput
                  source="shippingType"
                  label="배송타입"
                  choices={[
                    { id: 'FREE', name: '무료배송' },
                    { id: 'PAID', name: '유료배송' },
                  ]}
                  defaultValue="FREE"
                  validate={required()}
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <NumberInput
                  source="shippingFee"
                  label="배송비"
                  disabled={formData.shippingType === 'FREE'}
                  fullWidth
                />
              </Box>
            </Box>
          )}
        </FormDataConsumer>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          판매 설정
        </Typography>

        <BooleanInput
          source="isActive"
          label="판매 활성화"
          helperText="비활성화하면 상품이 판매중지 상태가 됩니다"
        />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          네이버 쇼핑 설정
        </Typography>

        <BooleanInput
          source="naverShoppingExposed"
          label="네이버 쇼핑 노출"
          helperText="네이버 쇼핑에 상품을 노출합니다"
        />

        <FormDataConsumer>
          {({ formData }) => formData.naverShoppingExposed && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                네이버 쇼핑 상세 설정
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    source="naverShoppingCategory"
                    label="네이버 카테고리 ID"
                    helperText="네이버 쇼핑 카테고리 코드"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    source="naverShoppingBrand"
                    label="네이버 브랜드명"
                    helperText="네이버에 표시될 브랜드명"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    source="naverShoppingModelNo"
                    label="모델번호"
                    helperText="상품 모델명/번호"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberInput
                    source="naverShipping"
                    label="배송비"
                    defaultValue={3000}
                    helperText="네이버 쇼핑 배송비 (기본 3,000원)"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SelectInput
                    source="naverCondition"
                    label="상품 상태"
                    choices={[
                      { id: '신상품', name: '신상품' },
                      { id: '중고', name: '중고' },
                      { id: '리퍼', name: '리퍼' },
                    ]}
                    defaultValue="신상품"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SelectInput
                    source="naverImportFlag"
                    label="수입 여부"
                    choices={[
                      { id: 'N', name: '국내산' },
                      { id: 'Y', name: '수입산' },
                    ]}
                    defaultValue="N"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    source="naverManufacturer"
                    label="제조사"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    source="naverOrigin"
                    label="원산지"
                    helperText="예: 대한민국, 중국 등"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SelectInput
                    source="naverAdult"
                    label="성인 상품"
                    choices={[
                      { id: 'N', name: '일반상품' },
                      { id: 'Y', name: '성인상품' },
                    ]}
                    defaultValue="N"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </FormDataConsumer>
      </SimpleForm>
    </Edit>
  );
};

// 상품 상세보기
export const ProductShow = () => {
  const ProductShowContent = () => {
    const record = useRecordContext();
    if (!record) return null;

    // 판매 상태 계산 (isActive와 stock 기반)
    const getStatus = () => {
      if (!record.isActive) {
        return { label: '판매중지', color: 'default' as const };
      }
      if (record.stock === 0) {
        return { label: '품절', color: 'error' as const };
      }
      return { label: '판매중', color: 'success' as const };
    };

    const status = getStatus();

    const discountRate = record.discountPrice && record.price
      ? Math.round(((record.price - record.discountPrice) / record.price) * 100)
      : 0;

    return (
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {/* 상품 기본 정보 */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <InfoOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                    상품 기본 정보
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="h4" fontWeight={700} mb={2} fontSize={{ xs: '1.5rem', sm: '1.75rem', md: '2rem' }}>
                  {record.name}
                </Typography>

                <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={1}>
                  {record.Category && (
                    <Chip
                      label={record.Category.name}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {record.brand && (
                    <Chip
                      label={record.brand}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Stack>

                {record.description && (
                  <Typography variant="body1" color="text.secondary" mb={2} fontSize={{ xs: '0.875rem', md: '1rem' }}>
                    {record.description}
                  </Typography>
                )}

                <Typography variant="caption" color="text.secondary">
                  상품 ID: {record.id}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 가격 정보 */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <AttachMoneyIcon color="success" fontSize="small" />
                  <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                    가격 정보
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      정가
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      fontSize={{ xs: '1.25rem', md: '1.5rem' }}
                      sx={{ textDecoration: record.discountPrice ? 'line-through' : 'none' }}
                    >
                      ₩{record.price?.toLocaleString()}
                    </Typography>
                  </Grid>

                  {record.discountPrice && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          할인가
                        </Typography>
                        <Typography variant="h5" fontWeight={600} color="error" fontSize={{ xs: '1.25rem', md: '1.5rem' }}>
                          ₩{record.discountPrice.toLocaleString()}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Chip
                          label={`${discountRate}% 할인`}
                          color="error"
                          size="small"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 재고 & 상태 */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <InventoryIcon color="warning" fontSize="small" />
                  <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                    재고 & 상태
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      재고 수량
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="primary" fontSize={{ xs: '1.75rem', md: '2.125rem' }}>
                      {record.stock}개
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      판매 상태
                    </Typography>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {record.isRecommended && (
                    <Chip label="추천상품" size="small" color="primary" variant="outlined" />
                  )}
                  {record.isBest && (
                    <Chip label="베스트상품" size="small" color="secondary" variant="outlined" />
                  )}
                  {record.isNew && (
                    <Chip label="신상품" size="small" color="info" variant="outlined" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* 상품 이미지 */}
          {record.imageUrl && (
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <ImageIcon color="info" fontSize="small" />
                    <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                      상품 이미지
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    component="img"
                    src={record.imageUrl}
                    alt={record.name}
                    sx={{
                      width: '100%',
                      maxHeight: { xs: 250, sm: 350, md: 400 },
                      objectFit: 'contain',
                      borderRadius: { xs: 1.5, md: 2 },
                      border: '1px solid #E0E0E0'
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* 판매 통계 */}
          <Grid item xs={12} md={record.imageUrl ? 6 : 12}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <BarChartIcon color="secondary" fontSize="small" />
                  <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                    판매 통계
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      조회수
                    </Typography>
                    <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                      {record.viewCount?.toLocaleString() || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      판매량
                    </Typography>
                    <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                      {record.soldCount?.toLocaleString() || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      찜 횟수
                    </Typography>
                    <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                      {record.wishlistCount?.toLocaleString() || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      리뷰 수
                    </Typography>
                    <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                      {record.reviewCount?.toLocaleString() || 0}
                    </Typography>
                  </Grid>

                  {record.rating && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <StarIcon sx={{ color: '#FFB400', fontSize: { xs: 20, md: 24 } }} />
                        <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                          {record.rating.toFixed(1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          / 5.0
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 네이버 쇼핑 정보 */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <InfoOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                    네이버 쇼핑 정보
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      네이버 쇼핑 노출
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={record.naverShoppingExposed ? '노출' : '미노출'}
                        color={record.naverShoppingExposed ? 'success' : 'default'}
                        size="small"
                        variant={record.naverShoppingExposed ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </Grid>

                  {record.naverShoppingCategory && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        네이버 쇼핑 카테고리
                      </Typography>
                      <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                        {record.naverShoppingCategory}
                      </Typography>
                    </Grid>
                  )}

                  {record.naverShoppingBrand && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        네이버 쇼핑 브랜드명
                      </Typography>
                      <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                        {record.naverShoppingBrand}
                      </Typography>
                    </Grid>
                  )}

                  {record.naverShoppingModelNo && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        모델번호
                      </Typography>
                      <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                        {record.naverShoppingModelNo}
                      </Typography>
                    </Grid>
                  )}

                  {record.naverShipping !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        배송비
                      </Typography>
                      <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                        {record.naverShipping?.toLocaleString()}원
                      </Typography>
                    </Grid>
                  )}

                  {record.naverCondition && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        상품 상태
                      </Typography>
                      <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                        {record.naverCondition}
                      </Typography>
                    </Grid>
                  )}

                  {record.naverManufacturer && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        제조사
                      </Typography>
                      <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                        {record.naverManufacturer}
                      </Typography>
                    </Grid>
                  )}

                  {record.naverOrigin && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        원산지
                      </Typography>
                      <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                        {record.naverOrigin}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 시스템 정보 */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CalendarTodayIcon color="action" fontSize="small" />
                  <Typography variant="h6" fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                    시스템 정보
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      등록일
                    </Typography>
                    <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                      {new Date(record.createdAt).toLocaleString('ko-KR')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      수정일
                    </Typography>
                    <Typography variant="body1" fontSize={{ xs: '0.875rem', md: '1rem' }}>
                      {new Date(record.updatedAt).toLocaleString('ko-KR')}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Show actions={<TopToolbar><EditButton label="편집" /></TopToolbar>}>
      <ProductShowContent />
    </Show>
  );
};